// Modules:
import React, { useRef, useState, useEffect, useContext } from 'react';

// Utilities
import { formatNumber, lightenColor } from 'utilities/utils';
import TopicConfigContext from 'utilities/topicConfigContext';

// Material UI:
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Chart.js:
import { Bar } from 'react-chartjs-2';

// Components:
import Selector from './Selector';

const HorizontalBar = ({
    json,
    selection,
    sortByAxis,
    dataUnavailable,
    setDataUnavailable,
    handleDropdownSelect,
    checkIfNull,
    styles,
    setMissingSuppressedModalOpen,
    setChartInfoModalOpen,
}) => {
    // // State // //
    const [chartData, setChartData] = useState({});

    // // Context // //
    const topicConfigContext = useContext(TopicConfigContext);

    // // Refs // //
    const chartRef = useRef();

    // // Effects // //

    // Load chart if component gets new data
    useEffect(() => {
        const loadChart = () => {
            // Reset defaults if chart loads for new data:
            setChartData({});
            setDataUnavailable(false);

            if (!json) {
                return;
            }

            const {
                baselineSettings: baseline,
                chartConfigs,
            } = topicConfigContext.tab.chartSettings;

            const getChartData = () => {
                const data = {}; // The object chart.js will be passed
                const yAxisField =
                    topicConfigContext.tab.chartSettings.chartYAxisField; // y-axis labels are are type names for this graph.
                const dataSetNames =
                    topicConfigContext.tab.chartSettings.chartDataSets;

                const datasetArray = [];

                // Sort labels alphabetically, always leaving baseline measure as first:
                const sortedData = sortByAxis(json, yAxisField, baseline);

                // Extract labels for chart from data:
                data.labels = sortedData.map((item) => item[yAxisField]);

                /* The following logic is specific for the Birth Defects health topic.  MDH wants the ability to toggle the
                'All Birth Defects' data and to accomplish this that data must exist as its own data set within the chart.
                This means that for the 'All Birth Defects' data set, the returned 'rate' value is maintained only for the
                'All Birth Defects' line, and the rest of the lines are set 'null'. This ensures that item count in each
                data set matches (required for this chart), the two data sets can be toggled on and off individually,
                and there are no duplicate values for any of the bars within the chart. */

                dataSetNames.forEach((setName) => {
                    const dataset = chartConfigs.find(
                        (x) => x.setName === setName
                    );

                    dataset.backgroundColor = [];
                    dataset.hoverBackgroundColor = [];

                    // The setName 'rate' relates to 'All Birth Defects'.
                    // 'All Birth Defects' is the baseline dataset for the 'Birth Defects' topic:
                    if (setName === 'rate') {
                        // Nullify all values EXCEPT 'All Birth Defects':
                        dataset.data = json.map((x) => {
                            if (x.chartLabel === 'All birth defects') {
                                return x[setName];
                            }
                            return null;
                        });

                        // Add primary colors to baseline dataset:
                        dataset.backgroundColor.push(dataset.color);
                        dataset.hoverBackgroundColor.push(
                            lightenColor(dataset.color)
                        );
                    }
                    if (setName === 'individualRate') {
                        // Nullify 'All Birth Defects' rate only:
                        dataset.data = json.map((x) => {
                            if (x.chartLabel !== 'All birth defects') {
                                return x[setName];
                            }
                            return null;
                        });

                        // Add secondary colors to non-primary dataset:
                        dataset.backgroundColor.push(dataset.color);
                        dataset.hoverBackgroundColor.push(
                            lightenColor(dataset.color)
                        );
                    }

                    // TODO: This is a quick fix to omit a property returned from the chartConfigs
                    // that is not needed for the chart.js dataset object for horizontal bar charts.
                    // This is better handled in the database.
                    delete dataset.borderWidth;

                    datasetArray.push(dataset);
                });

                // If all values are null, the following method will change `dataUnavailable` to true:
                const allNull = checkIfNull(datasetArray);
                if (allNull) {
                    setDataUnavailable(true);
                }

                data.datasets = datasetArray;

                return setChartData(data);
            };

            getChartData();
        };
        loadChart();
    }, [
        json,
        selection,
        checkIfNull,
        sortByAxis,
        topicConfigContext.tab,
        setDataUnavailable,
    ]);

    const dataYears = topicConfigContext.tab?.chartSettings.chartDataYears;

    return (
        <>
            <Grid container justify="center">
                {/* This check is to avoid null values being passed in as Select defaults which throws console warning */}
                {dataYears && selection && (
                    <Selector
                        currentTab={topicConfigContext.tab}
                        chartDataYears={dataYears}
                        handleDropdownSelect={handleDropdownSelect}
                        selection={selection}
                        styles={styles}
                    ></Selector>
                )}
            </Grid>
            {dataUnavailable ? (
                <Grid item xs={12}>
                    <div className="data-unavailable">
                        <Alert
                            sx={styles}
                            severity="info"
                            onClick={() => setMissingSuppressedModalOpen(true)}
                        >
                            {/* TODO: add year dynamically if this component is used by MDH */}
                            Data are not available to display for (year).
                        </Alert>
                    </div>
                </Grid>
            ) : (
                <Grid item xs={12}>
                    <div className="chart-container horizontal-jurisdiction-bars">
                        {Object.keys(chartData).length ? (
                            <Bar
                                ref={chartRef}
                                data={chartData}
                                width={100}
                                height={350}
                                options={{
                                    skipNull: true,
                                    indexAxis: 'y',
                                    maintainAspectRatio: false,
                                    plugins: {
                                        title: {
                                            display:
                                                topicConfigContext.tab
                                                    .chartSettings
                                                    .displayChartTitle,
                                            text: `${topicConfigContext.tab.chartSettings.chartTitle} (${selection})`,
                                        },
                                        subtitle: {
                                            display:
                                                topicConfigContext.tab
                                                    .chartSettings
                                                    .displayChartSubtitle ||
                                                false, // TODO:  The OR statements for these two keys are to account for a real subtitle only being in place for one topic.  Once the subtitle is given for all charts, this can be removed.
                                            text:
                                                topicConfigContext.tab
                                                    .chartSettings
                                                    .chartSubtitle || '',
                                        },
                                        tooltip: {
                                            mode: 'y',
                                            intersect: false,
                                            filter: (tooltipItem) =>
                                                // Removes non intersecting bar values from tooltip:
                                                tooltipItem.raw !== null,
                                            callbacks: {
                                                label: (tooltipItem) =>
                                                    formatNumber(
                                                        tooltipItem.raw
                                                    ),
                                            },
                                        },
                                        legend: {
                                            position: 'bottom',
                                        },
                                    },
                                    scales: {
                                        x: {
                                            title: {
                                                display:
                                                    topicConfigContext.tab
                                                        .chartSettings
                                                        .displayXAxisLabel,
                                                text:
                                                    topicConfigContext.tab
                                                        .chartSettings
                                                        .xAxisLabel,
                                            },
                                        },
                                    },
                                }}
                            />
                        ) : null}
                    </div>
                    {Object.keys(chartData).length ? (
                        <div className="chart-info-modal-trigger-container">
                            <Button
                                size="small"
                                onClick={() => setChartInfoModalOpen(true)}
                            >
                                <InfoOutlinedIcon />
                            </Button>
                        </div>
                    ) : null}
                </Grid>
            )}
        </>
    );
};

export default HorizontalBar;
